import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerInputEvent, MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TimeService } from '@services/common/time.service';
import {
  MyDateAdapter,
  PeriodCalendarService
} from '@shared/components/scheduler-period-picker/period-picker-calendar/period-picker-calendar.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface PeriodDateObj {
  fromDate: string;
  toDate: string;
  period: string;
  type: string;
}

@Component({
  selector: 'period-picker-calendar',
  templateUrl: './period-picker-calendar.component.html',
  styleUrls: ['./period-picker-calendar.component.scss'],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: PeriodCalendarService
    },
    { provide: DateAdapter, useClass: MyDateAdapter }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodPickerCalendarComponent implements OnInit, OnChanges, OnDestroy {
  range: UntypedFormGroup;

  destroy$ = new Subject();

  @Input() fromDate: any;
  @Input() toDate: any;
  @Input() period: string;
  @Input() isMobile: boolean = false;
  @Output() dateChange = new EventEmitter();

  startChange(event: MatDatepickerInputEvent<Date>) {
    this.fromDate = event.value;
  }

  endChange(event: MatDatepickerInputEvent<Date>) {
    let periodDateObj: PeriodDateObj;

    this.toDate = event.value;
    periodDateObj = this.periodFactory(
      this._timeService.formatDate(this.fromDate),
      this._timeService.formatDate(this.toDate),
      this.period,
      'period'
    );
    this.dateChange.next(periodDateObj);

    this.initRange(periodDateObj);
  }

  initRange(periodDateObj?) {
    this.range = new UntypedFormGroup({
      start: new UntypedFormControl(!periodDateObj ? this.fromDate : periodDateObj.fromDate),
      end: new UntypedFormControl(!periodDateObj ? this.toDate : periodDateObj.toDate)
    });
  }

  ngOnChanges(): void {
    this.initRange();
    this.initSelectedPeriod();
  }

  ngOnInit(): void {
    this._translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((a) => {
      this._adapter.setLocale(this._translate.currentLang);
    });

    this.initRange();
    this.initSelectedPeriod();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private initSelectedPeriod(): void {
    this._periodCalendarService.selectedPeriod$.next(this.period);
  }

  private periodFactory(from, to, periodType, type): PeriodDateObj {
    return {
      fromDate: from,
      toDate: to,
      period: periodType,
      type: type
    };
  }

  constructor(
    private _adapter: DateAdapter<Date>,
    private _translate: TranslateService,
    private _dateAdapter: DateAdapter<Date>,
    private _timeService: TimeService,
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private _periodCalendarService: PeriodCalendarService<Date>
  ) {}
}
